/**
 * Cache for presigned URLs with expiration tracking
 */

interface CachedUrl {
    url: string;
    expiresAt: number; // timestamp in milliseconds
}

class PresignedUrlCache {
    private cache: Map<string, CachedUrl> = new Map();
    private readonly SAFETY_MARGIN = 30 * 1000; // 30 seconds safety margin before expiration

    /**
     * Generate a cache key for the presigned URL
     */
    private getCacheKey(action: 'upload' | 'download', s3Key: string): string {
        return `${action}:${s3Key}`;
    }

    /**
     * Check if a cached URL exists and is still valid
     */
    public has(action: 'upload' | 'download', s3Key: string): boolean {
        const key = this.getCacheKey(action, s3Key);
        const cached = this.cache.get(key);
        
        if (!cached) {
            return false;
        }

        // Check if URL has expired (with safety margin)
        const now = Date.now();
        if (now >= cached.expiresAt - this.SAFETY_MARGIN) {
            // URL expired or about to expire, remove from cache
            this.cache.delete(key);
            return false;
        }

        return true;
    }

    /**
     * Get a cached URL if it exists and is valid
     */
    public get(action: 'upload' | 'download', s3Key: string): string | null {
        const key = this.getCacheKey(action, s3Key);
        const cached = this.cache.get(key);
        
        if (!cached) {
            return null;
        }

        // Check if URL has expired (with safety margin)
        const now = Date.now();
        if (now >= cached.expiresAt - this.SAFETY_MARGIN) {
            // URL expired or about to expire, remove from cache
            this.cache.delete(key);
            return null;
        }

        console.log(`Using cached presigned URL for ${action} (expires in ${Math.round((cached.expiresAt - now) / 1000)}s)`);
        return cached.url;
    }

    /**
     * Store a presigned URL in the cache
     * @param action - The action type (upload or download)
     * @param s3Key - The S3 key
     * @param url - The presigned URL
     * @param expiresIn - How long the URL is valid for (in seconds)
     */
    public set(action: 'upload' | 'download', s3Key: string, url: string, expiresIn: number): void {
        const key = this.getCacheKey(action, s3Key);
        const expiresAt = Date.now() + (expiresIn * 1000);
        
        this.cache.set(key, {
            url,
            expiresAt
        });

        console.log(`Cached presigned URL for ${action} (expires in ${expiresIn}s)`);
    }

    /**
     * Clear the entire cache
     */
    public clear(): void {
        this.cache.clear();
    }

    /**
     * Clean up expired URLs from the cache
     */
    public cleanExpired(): void {
        const now = Date.now();
        for (const [key, cached] of this.cache.entries()) {
            if (now >= cached.expiresAt - this.SAFETY_MARGIN) {
                this.cache.delete(key);
            }
        }
    }
}

// Export a singleton instance
export const presignedUrlCache = new PresignedUrlCache();